"use strict";
import posthog from "posthog-js";
import { sampleByDistinctId } from "posthog-js/lib/src/customizations/before-send";
const apiKey = process.env.REACT_APP_PUBLIC_POSTHOG_KEY;
const host = process.env.REACT_APP_PUBLIC_POSTHOG_HOST;
const isConfigured = Boolean(apiKey && host);
const DEFAULT_SAMPLE_RATE = 0.1;
const PERSON_ID_STORAGE_KEY = "topaz_posthog_person_id";
const WALLET_ADDRESSES_STORAGE_KEY = "topaz_posthog_wallet_addresses";
let activeWalletAddress;
let lastIdentifiedPersonId;
const sampledPostHogEventNames = /* @__PURE__ */ new Set([
  "gauge_vote_submitted",
  "incentive_created",
  "liquidity_submitted",
  "lock_created",
  "swap_confirmed",
  "swap_failed",
  "swap_submitted",
  "wallet_connected",
  "wallet_disconnected"
]);
function parseSampleRate(value) {
  if (value === void 0) {
    return DEFAULT_SAMPLE_RATE;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return DEFAULT_SAMPLE_RATE;
  }
  return Math.min(1, Math.max(0, parsed));
}
const postHogSampleRate = parseSampleRate(process.env.REACT_APP_PUBLIC_POSTHOG_SAMPLE_RATE);
const samplePostHogEventByDistinctId = sampleByDistinctId(postHogSampleRate);
function sampleTopazPostHogEvent(captureResult) {
  if (!captureResult || !sampledPostHogEventNames.has(captureResult.event)) {
    return captureResult;
  }
  const sampledCaptureResult = samplePostHogEventByDistinctId(captureResult);
  if (!sampledCaptureResult) {
    return null;
  }
  return {
    ...sampledCaptureResult,
    properties: {
      ...sampledCaptureResult.properties,
      posthog_sample_rate: postHogSampleRate
    }
  };
}
function getStoredValue(key) {
  try {
    return window.localStorage.getItem(key) ?? void 0;
  } catch {
    return void 0;
  }
}
function setStoredValue(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
  }
}
function getStoredWalletAddresses() {
  const stored = getStoredValue(WALLET_ADDRESSES_STORAGE_KEY);
  if (!stored) {
    return [];
  }
  try {
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed.filter((value) => typeof value === "string") : [];
  } catch {
    return [];
  }
}
function addStoredWalletAddress(address) {
  const walletAddresses = Array.from(/* @__PURE__ */ new Set([...getStoredWalletAddresses(), address]));
  setStoredValue(WALLET_ADDRESSES_STORAGE_KEY, JSON.stringify(walletAddresses));
  return walletAddresses;
}
function resolvePostHogPersonId({
  address,
  privyUserId
}) {
  if (privyUserId) {
    const personId2 = `privy:${privyUserId}`;
    setStoredValue(PERSON_ID_STORAGE_KEY, personId2);
    return personId2;
  }
  const storedPersonId = getStoredValue(PERSON_ID_STORAGE_KEY);
  if (storedPersonId) {
    return storedPersonId;
  }
  const personId = `wallet:${address}`;
  setStoredValue(PERSON_ID_STORAGE_KEY, personId);
  return personId;
}
if (apiKey && host) {
  posthog.init(apiKey, {
    api_host: host,
    // 'history_change' fires $pageview on SPA route changes too, not just the
    // initial load — without it PostHog Web Analytics has no data to show.
    capture_pageview: "history_change",
    capture_pageleave: true,
    autocapture: false,
    before_send: sampleTopazPostHogEvent,
    // Disabled: wallet connector SDKs (MetaMask/Coinbase) emit benign unhandled rejections during
    // wagmi's mount-time auto-reconnect; exception autocapture surfaces them as uncaught errors.
    capture_exceptions: false,
    disable_session_recording: true,
    person_profiles: "identified_only",
    persistence: "localStorage+cookie",
    loaded: (ph) => {
      if (process.env.NODE_ENV === "development") {
        ph.debug();
      }
    }
  });
}
export { posthog };
export const isPostHogEnabled = isConfigured;
export function capturePostHogEvent(eventName, properties) {
  if (!isConfigured) {
    return;
  }
  posthog.capture(eventName, {
    ...properties,
    ...activeWalletAddress ? { $groups: { wallet: activeWalletAddress } } : void 0
  });
}
export function identifyPostHogPerson({
  address,
  connectorId,
  privyUserId
}) {
  if (!isConfigured) {
    return void 0;
  }
  const normalizedAddress = address.toLowerCase();
  const personId = resolvePostHogPersonId({ address: normalizedAddress, privyUserId });
  const walletAddresses = addStoredWalletAddress(normalizedAddress);
  activeWalletAddress = normalizedAddress;
  posthog.register({
    active_wallet_address: normalizedAddress,
    active_wallet_connector_id: connectorId,
    posthog_person_id: personId
  });
  const personProperties = {
    active_wallet_address: normalizedAddress,
    active_wallet_connector_id: connectorId,
    has_topaz_id: Boolean(privyUserId),
    topaz_privy_user_id: privyUserId,
    wallet_addresses: walletAddresses
  };
  if (lastIdentifiedPersonId !== personId) {
    posthog.identify(personId, personProperties);
    lastIdentifiedPersonId = personId;
  } else {
    posthog.setPersonProperties(personProperties);
  }
  return personId;
}
export function clearPostHogWallet() {
  if (!isConfigured) {
    return;
  }
  activeWalletAddress = void 0;
  posthog.unregister("active_wallet_address");
  posthog.unregister("active_wallet_connector_id");
}
